import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface SearchableSelectProps {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  label,
  options,
  value,
  onChange,
  placeholder = 'Search or select…',
  required,
  error,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = query
    ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (option: string) => {
    onChange(option);
    setOpen(false);
    setQuery('');
  };

  const handleInputClick = () => {
    if (!disabled) {
      setOpen(true);
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (!open) setOpen(true);
  };

  return (
    <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column' }}>
      <label className="lbl">
        {label}
        {required && <span style={{ color: 'var(--err)', marginLeft: '2px' }}>*</span>}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={open ? query : value}
          placeholder={value || placeholder}
          onChange={handleInputChange}
          onClick={handleInputClick}
          readOnly={disabled}
          className={['inp', error ? 'err-inp' : ''].filter(Boolean).join(' ')}
          style={{
            paddingRight: '36px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            background: disabled ? 'var(--surface-alt)' : 'var(--surface)',
            borderColor: open ? 'var(--p)' : undefined,
          }}
        />
        <span
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
            pointerEvents: 'none',
            transition: 'transform 0.15s',
            color: 'var(--text-2)',
            display: 'flex',
          }}
        >
          <ChevronDown size={16} />
        </span>

        {open && !disabled && (
          <div className="dropdown-menu" style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, maxHeight: '220px', overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '10px 12px', color: 'var(--text-3)', fontSize: '13px' }}>
                No options found
              </div>
            ) : (
              filtered.map((option) => (
                <button
                  key={option}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(option); }}
                  className="dropdown-item"
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    background: option === value ? 'var(--p-light)' : undefined,
                    color: option === value ? 'var(--p)' : undefined,
                    fontWeight: option === value ? 600 : 400,
                  }}
                >
                  {option}
                </button>
              ))
            )}
          </div>
        )}
      </div>
      {error && <span className="err-msg">{error}</span>}
    </div>
  );
};
